import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

export type MfaMethodType = 0 | 1 | 2

export interface MfaStatus {
  isMfaEnforced: boolean
  showGracePeriodBanner: boolean
  daysRemaining?: number
  enforcementDeadlineUtc?: string
  activeMethodCount: number
  isEnrolled: boolean
  requiresSetup: boolean
}

export interface MfaMethodItem {
  method: MfaMethodType
  methodName: string
  status: string
  isEnabled: boolean
  contactMasked?: string
  contactValue?: string
  lastUsedAt?: string
  verifiedAt?: string
  canSetUp: boolean
  canToggle: boolean
  subtext: string
}

export interface MfaMethodsResponse {
  activeMethodCount: number
  methods: MfaMethodItem[]
  isAdminView: boolean
}

export interface MfaDisableMethodResponse {
  success: boolean
  sessionInvalidated: boolean
  remainingActiveMethods: number
  token?: string
  refreshToken?: string
  refreshTokenExpiration?: string
  roles?: string[]
  email?: string
  userName?: string
  isAllowedMobileLogin?: boolean
}

export const getMfaStatus = (userId?: string) =>
  axios.get<MfaStatus>(`${API_URL}/mfa/status`, {params: userId ? {userId} : {}})

export const setMfaEnforcement = (enabled: boolean, userId?: string) =>
  axios.put(`${API_URL}/mfa/enforcement`, {enabled, userId})

export const getMfaMethods = (userId?: string) =>
  axios.get<MfaMethodsResponse>(`${API_URL}/mfa/methods`, {params: userId ? {userId} : {}})

export const startMfaEnrollment = (payload: {
  method: MfaMethodType
  contactValue?: string
  mfaSession?: string
  targetUserId?: string
}) => axios.post(`${API_URL}/mfa/enroll/start`, payload)

export const verifyMfaEnrollment = (payload: {
  method: MfaMethodType
  code: string
  mfaSession?: string
  targetUserId?: string
}) => axios.post(`${API_URL}/mfa/enroll/verify`, payload)

export const disableMfaMethod = (method: MfaMethodType, targetUserId?: string) =>
  axios.post<MfaDisableMethodResponse>(`${API_URL}/mfa/methods/${method}/disable`, {targetUserId})

export const selectMfaChallengeMethod = (method: MfaMethodType, mfaSession: string) =>
  axios.post(`${API_URL}/mfa/challenge/select-method`, {method, mfaSession})

export const respondMfaChallenge = (code: string, mfaSession: string) =>
  axios.post(`${API_URL}/mfa/challenge/respond`, {code, mfaSession})

export const resendMfaChallenge = (mfaSession: string) =>
  axios.post(`${API_URL}/mfa/challenge/resend`, {mfaSession})

export const methodLabel = (method: MfaMethodType) => mfaMethodMeta(method).title

export const mfaMethodMeta = (method: MfaMethodType) => {
  switch (method) {
    case 0:
      return {
        title: 'Text message',
        description: 'Get a code sent to your number',
        icon: 'bi-chat-dots-fill',
        iconStyle: {backgroundColor: '#F1F1F4', color: '#78829D'},
        selectedIconStyle: {backgroundColor: '#D7383C', color: '#FFFFFF'},
      }
    case 1:
      return {
        title: 'Email',
        description: 'Get a code sent to your email address',
        icon: 'bi-envelope-fill',
        iconStyle: {backgroundColor: '#EEF0FF', color: '#7239EA'},
        selectedIconStyle: {backgroundColor: '#7239EA', color: '#FFFFFF'},
      }
    default:
      return {
        title: 'Authenticator App',
        description: 'Google Authenticator, Microsoft Authenticator, etc.',
        icon: 'bi-phone-fill',
        iconStyle: {backgroundColor: '#F1F1F4', color: '#78829D'},
        selectedIconStyle: {backgroundColor: '#78829D', color: '#FFFFFF'},
      }
  }
}
